import { useState } from "react";
import { motion } from "framer-motion";
import { ChevronLeft, ChevronRight, Quote } from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";

export const TestimonialsSection = ({ testimonials, loading }) => {
  const [currentIndex, setCurrentIndex] = useState(0);

  const nextTestimonial = () => {
    setCurrentIndex((prev) => (prev + 1) % testimonials.length);
  };

  const prevTestimonial = () => {
    setCurrentIndex((prev) => (prev - 1 + testimonials.length) % testimonials.length);
  };

  return (
    <section
      id="testimonials"
      className="section-dark py-24 md:py-32"
      data-testid="testimonials-section"
    >
      <div className="container-custom">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <p className="text-gold/80 font-manrope text-sm tracking-[0.3em] uppercase mb-4">
            Words of Grace
          </p>
          <h2 className="font-cinzel text-4xl md:text-5xl lg:text-6xl font-semibold text-white mb-6">
            <span className="text-gold">Testimonials</span>
          </h2>
          <div className="section-divider mx-auto" />
        </motion.div>

        {/* Testimonials Carousel */}
        {loading ? (
          <div className="max-w-3xl mx-auto text-center">
            <Skeleton className="h-6 w-full bg-white/10 mb-4" />
            <Skeleton className="h-6 w-3/4 mx-auto bg-white/10 mb-4" />
            <Skeleton className="h-4 w-1/2 mx-auto bg-white/10" />
          </div>
        ) : testimonials.length > 0 ? (
          <div className="relative max-w-4xl mx-auto">
            {/* Navigation Buttons */}
            {testimonials.length > 1 && (
              <>
                <button
                  onClick={prevTestimonial}
                  className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-12 text-gold/60 hover:text-gold transition-colors hidden md:block"
                  data-testid="prev-testimonial"
                >
                  <ChevronLeft size={40} />
                </button>
                <button
                  onClick={nextTestimonial}
                  className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-12 text-gold/60 hover:text-gold transition-colors hidden md:block"
                  data-testid="next-testimonial"
                >
                  <ChevronRight size={40} />
                </button>
              </>
            )}

            {/* Testimonial Card */}
            <motion.div
              key={currentIndex}
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5 }}
              className="testimonial-card p-8 md:p-12 text-center"
              data-testid={`testimonial-${currentIndex}`}
            >
              {/* Quote Icon */}
              <div className="quote-icon mb-6">
                <Quote className="w-12 h-12 text-gold/30 mx-auto" />
              </div>

              {/* Testimonial Text */}
              <p className="font-playfair text-xl md:text-2xl text-white/80 italic leading-relaxed mb-8">
                "{testimonials[currentIndex].text}"
              </p>

              {/* Author */}
              <div className="space-y-2">
                <p className="font-cinzel text-lg text-gold">
                  {testimonials[currentIndex].name}
                </p>
                {testimonials[currentIndex].location && (
                  <p className="font-manrope text-sm text-white/50">
                    {testimonials[currentIndex].location}
                  </p>
                )}
              </div>
            </motion.div>

            {/* Dots Navigation */}
            {testimonials.length > 1 && (
              <div className="flex justify-center gap-3 mt-8">
                {testimonials.map((_, index) => (
                  <button
                    key={index}
                    onClick={() => setCurrentIndex(index)}
                    className={`w-2 h-2 rounded-full transition-all ${
                      index === currentIndex
                        ? "bg-gold w-8"
                        : "bg-white/20 hover:bg-white/40"
                    }`}
                    data-testid={`testimonial-dot-${index}`}
                  />
                ))}
              </div>
            )}

            {/* Mobile Navigation */}
            {testimonials.length > 1 && (
              <div className="flex justify-center gap-4 mt-6 md:hidden">
                <button
                  onClick={prevTestimonial}
                  className="text-gold/60 hover:text-gold transition-colors p-2"
                  data-testid="prev-testimonial-mobile"
                >
                  <ChevronLeft size={32} />
                </button>
                <button
                  onClick={nextTestimonial}
                  className="text-gold/60 hover:text-gold transition-colors p-2"
                  data-testid="next-testimonial-mobile"
                >
                  <ChevronRight size={32} />
                </button>
              </div>
            )}
          </div>
        ) : (
          <p className="text-center text-white/50 font-manrope">
            No testimonials available yet.
          </p>
        )}
      </div>
    </section>
  );
};

export default TestimonialsSection;
